import { motion } from "framer-motion";
import { ExternalLink, Phone, Mail } from "lucide-react";
import { FloatingCube } from "@/three/ProductStage";
import { track } from "@/lib/analytics";
import { Wordmark } from "@/components/Navbar";

export function FinalCta() {
    return (
        <section
            id="contacto"
            data-testid="final-cta-section"
            className="grain relative overflow-hidden bg-[#0A192F] py-28 md:py-40"
        >
            <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-2 lg:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8 }}
                >
                    <h2 className="font-display text-4xl font-extrabold leading-[1.02] tracking-tighter text-white sm:text-5xl lg:text-6xl" data-testid="final-cta-headline">
                        ¿BUSCAS LYHER<span className="align-super text-[0.5em]">®</span> EN{" "}
                        <span className="text-[#57A632]">CHILE?</span>
                    </h2>
                    <p className="mt-6 max-w-md text-base leading-relaxed text-white/60 md:text-lg">
                        Conoce disponibilidad y soluciones para tu organización.
                    </p>

                    <div className="mt-10 flex flex-wrap gap-4">
                        <a
                            href="https://www.detex.cl"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => track("click_detex", { partner: "detex", destination: "website", cta_location: "final_cta" })}
                            data-testid="final-cta-detex"
                            className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-extrabold tracking-wider text-[#0A192F] transition-[background-color,color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#57A632] hover:text-white"
                        >
                            <ExternalLink size={15} />
                            DETEX.CL
                        </a>
                        <a
                            href="https://www.cincosalud.cl"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => track("click_cincosalud", { partner: "cincosalud", destination: "website", cta_location: "final_cta" })}
                            data-testid="final-cta-cincosalud"
                            className="inline-flex items-center gap-2 rounded-full border border-white/25 px-8 py-4 text-sm font-extrabold tracking-wider text-white transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5 hover:border-[#57A632] hover:bg-[#57A632]/10"
                        >
                            <ExternalLink size={15} />
                            CINCOSALUD.CL
                        </a>
                    </div>

                    <div className="mt-12 grid max-w-md grid-cols-1 gap-4 text-sm text-white/70 sm:grid-cols-2">
                        <div className="space-y-2">
                            <a href="tel:+56967273223" onClick={() => track("click_phone_detex", { partner: "detex", destination: "phone", cta_location: "final_cta" })} data-testid="final-phone-detex" className="flex items-center gap-2 transition-colors duration-200 hover:text-[#57A632]">
                                <Phone size={14} className="text-[#57A632]" /> +56 9 6727 3223
                            </a>
                            <a href="mailto:ventas@detex.cl" onClick={() => track("click_email_detex", { partner: "detex", destination: "email", cta_location: "final_cta" })} data-testid="final-email-detex" className="flex items-center gap-2 transition-colors duration-200 hover:text-[#57A632]">
                                <Mail size={14} className="text-[#57A632]" /> ventas@detex.cl
                            </a>
                        </div>
                        <div className="space-y-2">
                            <a href="tel:+56990119887" onClick={() => track("click_phone_cincosalud", { partner: "cincosalud", destination: "phone", cta_location: "final_cta" })} data-testid="final-phone-cincosalud" className="flex items-center gap-2 transition-colors duration-200 hover:text-[#57A632]">
                                <Phone size={14} className="text-[#57A632]" /> +56 9 9011 9887
                            </a>
                            <a href="mailto:ventas@cincosalud.cl" onClick={() => track("click_email_cincosalud", { partner: "cincosalud", destination: "email", cta_location: "final_cta" })} data-testid="final-email-cincosalud" className="flex items-center gap-2 transition-colors duration-200 hover:text-[#57A632]">
                                <Mail size={14} className="text-[#57A632]" /> ventas@cincosalud.cl
                            </a>
                        </div>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.92 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.9 }}
                    className="relative h-[400px] md:h-[520px]"
                >
                    <FloatingCube className="h-full w-full" />
                </motion.div>
            </div>
        </section>
    );
}

export function Footer() {
    return (
        <footer data-testid="footer" className="relative border-t border-white/10 bg-[#060F1F] py-16">
            <div className="mx-auto max-w-7xl px-6 lg:px-10">
                <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4">
                    <div>
                        <Wordmark light />
                        <p className="mt-4 max-w-xs text-sm leading-relaxed text-white/50">
                            Oral Fluid Multi-Drug Test Kit (Cube). Tecnología de detección rápida en fluido oral con
                            FDA 510(k).
                        </p>
                    </div>
                    <div>
                        <p className="mb-5 text-[11px] font-bold tracking-[0.24em] text-white/40">PRODUCTO</p>
                        <ul className="space-y-3 text-sm text-white/60">
                            {[
                                ["Producto", "#producto"],
                                ["FDA", "#fda"],
                                ["Cómo funciona", "#como-funciona"],
                                ["Chile", "#chile"],
                                ["Contacto", "#contacto"],
                            ].map(([label, href]) => (
                                <li key={href}>
                                    <a href={href} className="transition-colors duration-200 hover:text-[#57A632]">
                                        {label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <p className="mb-5 text-[11px] font-bold tracking-[0.24em] text-white/40">DETEX</p>
                        <ul className="space-y-3 text-sm text-white/60">
                            <li>
                                <a href="https://www.detex.cl" target="_blank" rel="noopener noreferrer" className="transition-colors duration-200 hover:text-[#57A632]">
                                    www.detex.cl
                                </a>
                            </li>
                            <li>
                                <a href="tel:+56967273223" className="transition-colors duration-200 hover:text-[#57A632]">
                                    +56 9 6727 3223
                                </a>
                            </li>
                            <li>
                                <a href="mailto:ventas@detex.cl" className="transition-colors duration-200 hover:text-[#57A632]">
                                    ventas@detex.cl
                                </a>
                            </li>
                        </ul>
                    </div>
                    <div>
                        <p className="mb-5 text-[11px] font-bold tracking-[0.24em] text-white/40">CINCO SALUD</p>
                        <ul className="space-y-3 text-sm text-white/60">
                            <li>
                                <a href="https://www.cincosalud.cl" target="_blank" rel="noopener noreferrer" className="transition-colors duration-200 hover:text-[#57A632]">
                                    www.cincosalud.cl
                                </a>
                            </li>
                            <li>
                                <a href="tel:+56990119887" className="transition-colors duration-200 hover:text-[#57A632]">
                                    +56 9 9011 9887
                                </a>
                            </li>
                            <li>
                                <a href="mailto:ventas@cincosalud.cl" className="transition-colors duration-200 hover:text-[#57A632]">
                                    ventas@cincosalud.cl
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
                <div className="mt-14 flex flex-col gap-3 border-t border-white/10 pt-8 text-xs text-white/40 md:flex-row md:items-center md:justify-between">
                    <p>LYHER® es una marca de Hangzhou Laihe Biotech Co., Ltd.</p>
                    <p>© 2026 — LYHER® Oral Fluid Multi-Drug Test Kit en Chile</p>
                </div>
                <div className="mt-8 flex justify-center">
                    <a
                        href="https://www.devoluzion.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="footer-devoluzion"
                        className="group inline-flex items-center gap-3 text-xs text-white/40 transition-colors duration-300 hover:text-white/70"
                    >
                        Sitio creado por
                        <img
                            src="/assets/logo-devoluzion.webp"
                            alt="Devoluzion"
                            width="110"
                            height="22"
                            loading="lazy"
                            className="h-5 w-auto opacity-60 transition-opacity duration-300 group-hover:opacity-100"
                        />
                    </a>
                </div>
            </div>
        </footer>
    );
}
